/**
 * User Utility Functions
 * Helper functions for user data formatting and filtering
 */

import { getUserRoles, isAdmin, isTeamLead, getTeamAccessLevel } from './roleUtils';
import { getRoleLabel } from './roleConfig';

/**
 * Format user for display in lists/dropdowns
 */
export const formatUserForDisplay = (user) => {
  if (!user) return null;
  
  return {
    id: user.id,
    name: user.full_name || user.username || 'Unknown',
    email: user.email || 'N/A',
    role: user.role,
    roles: getUserRoles(user),
    roleLabels: getUserRoles(user).map(getRoleLabel).join(', '),
    isActive: user.is_active !== false,
    department: user.department || 'N/A'
  };
};

/**
 * Filter users by role
 */
export const filterUsersByRole = (users, roleValue) => {
  if (!Array.isArray(users)) return [];
  if (!roleValue || roleValue === 'all') return users;
  
  return users.filter(user => {
    const userRoles = getUserRoles(user);
    return userRoles.includes(roleValue);
  });
};

/**
 * Filter users by search term
 */
export const filterUsersBySearch = (users, searchTerm) => {
  if (!Array.isArray(users)) return [];
  if (!searchTerm || searchTerm.trim() === '') return users;
  
  const term = searchTerm.toLowerCase().trim();
  
  return users.filter(user => {
    const name = (user.full_name || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    
    return name.includes(term) || username.includes(term) || email.includes(term);
  });
};

/**
 * Sort users by name
 */
export const sortUsersByName = (users) => {
  if (!Array.isArray(users)) return [];
  
  return [...users].sort((a, b) => {
    const nameA = (a.full_name || a.username || '').toLowerCase();
    const nameB = (b.full_name || b.username || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Get users by team
 */
export const getUsersByTeam = (users, teamId) => {
  if (!Array.isArray(users)) return [];
  if (!teamId) return [];
  
  return users.filter(user => user.team_id === teamId);
};

/**
 * Get available team members for a team lead
 */
export const getAvailableTeamMembers = (allUsers, teamLead, existingTeams) => {
  if (!allUsers || !teamLead) return [];
  
  // Get all users who are already in teams
  const usersInTeams = new Set();
  const teamLeaders = new Set();
  
  if (existingTeams && Array.isArray(existingTeams)) {
    existingTeams.forEach(team => {
      if (team.team_lead_id) {
        teamLeaders.add(team.team_lead_id);
      }
      if (team.team_members && Array.isArray(team.team_members)) {
        team.team_members.forEach(memberId => usersInTeams.add(memberId));
      }
    });
  }
  
  // Filter available users based on team lead's role
  return allUsers.filter(user => {
    // Don't include self
    if (user.id === teamLead.id) return false;
    
    // Don't include users already in teams or leading teams
    if (usersInTeams.has(user.id) || teamLeaders.has(user.id)) return false;
    
    // Recruiter Supervisor can add Lead Recruiters and Recruiters
    if (teamLead.role === 'recruiter_supervisor') {
      return user.role === 'lead_recruiter' || user.role === 'recruiter';
    }
    
    // Lead Recruiter can add Recruiters
    if (teamLead.role === 'lead_recruiter') {
      return user.role === 'recruiter';
    }
    
    // Account Manager can add Recruiters, Lead Recruiters, and Account Execs
    if (teamLead.role === 'account_manager') {
      return user.role === 'recruiter' || user.role === 'lead_recruiter' || user.role === 'account_exec';
    }
    
    return false;
  });
};

/**
 * Get user statistics
 */
export const getUserStatistics = (users) => {
  if (!Array.isArray(users)) {
    return {
      total: 0,
      active: 0,
      inactive: 0,
      byRole: {},
      teamLeads: 0,
      teamMembers: 0
    };
  }
  
  const stats = {
    total: users.length,
    active: 0,
    inactive: 0,
    byRole: {},
    teamLeads: 0,
    teamMembers: 0
  };
  
  users.forEach(user => {
    // Count active/inactive
    if (user.is_active !== false) {
      stats.active++;
    } else {
      stats.inactive++;
    }
    
    // Count by role
    const userRoles = getUserRoles(user);
    userRoles.forEach(role => {
      stats.byRole[role] = (stats.byRole[role] || 0) + 1;
    });
    
    // Count team leads
    if (isTeamLead(user)) {
      stats.teamLeads++;
    }
  });
  
  return stats;
};

/**
 * Validate user data
 */
export const validateUserData = (userData) => {
  const errors = [];
  
  if (!userData.email || !userData.email.includes('@')) {
    errors.push('Valid email is required');
  }
  
  if (!userData.full_name || userData.full_name.trim().length < 2) {
    errors.push('Full name must be at least 2 characters');
  }
  
  if (!userData.role) {
    errors.push('Role is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
